import focusLock from "dom-focus-lock"
import throttle from "lodash-es/throttle"

type Props = {
  topBarSelector: string
  maskActiveClass: string
  hamburgerToggleSelector: string
  stickyBackgroundColor: string
  mobileActiveClass: string
  menuSelector: string
  backgroundColor: string
}

export default function mastheadSimplified(
  el: HTMLElement,
  {
    topBarSelector,
    maskActiveClass,
    hamburgerToggleSelector,
    mobileActiveClass,
    menuSelector,
    backgroundColor,
    stickyBackgroundColor,
  }: Props
) {
  const topBarEl = el.querySelector(topBarSelector)
  const topBarHeight = topBarEl?.getBoundingClientRect().height || 40
  const hamburgerToggleEl: HTMLElement | null = el.querySelector(
    hamburgerToggleSelector
  )
  const menuEl: HTMLElement | null = el.querySelector(menuSelector)
  const bodyEl: HTMLBodyElement | null = document.querySelector("body")

  if (!hamburgerToggleEl || !menuEl || !bodyEl) {
    return
  }

  let navIsActive = false

  const mediaQuery = window.matchMedia("(min-width: 1200px)")

  mediaQuery.addEventListener("change", checkMedia)

  function checkMedia(e: MediaQueryListEvent) {
    if (!hamburgerToggleEl || !menuEl || !bodyEl) {
      return
    }
    if (e.matches) {
      hideMobileMenu()
    }
  }

  initializeMasthead()

  function initializeMasthead() {
    // load nav when starting the page
    loadNavProperties()

    // load/updates nav on scroll
    if (backgroundColor && backgroundColor !== "") {
      // Toggle masthead background color when (throttled) scrolling
      document.addEventListener(
        "scroll",
        throttle(function () {
          loadNavProperties()
        }, 10)
      )

      el.addEventListener("mouseout", function (e) {
        if (window.scrollY <= topBarHeight) {
          if (!navIsActive) {
            addNavBgColor(backgroundColor)
          }
        }
      })
    }

    // Hamburger (mobile) menu toggle
    if (hamburgerToggleEl) {
      hamburgerToggleEl.addEventListener("click", function () {
        toggleMobileMenu()
      })
    }

    // Close menu on esc
    document.addEventListener("keyup", function (e) {
      if (27 === e.keyCode && "Escape" === e.key) {
        if (navIsActive) {
          hideMobileMenu()
        }
      }
    })

    toggleMask()
  }

  function toggleMobileMenu() {
    if (navIsActive) {
      hideMobileMenu()
    } else {
      showMobileMenu()
    }
  }

  function showMobileMenu() {
    el.classList.add(mobileActiveClass)
    el.classList.add(maskActiveClass)
    if (!hamburgerToggleEl || !bodyEl || !menuEl) {
      return
    }
    hamburgerToggleEl.setAttribute("aria-expanded", "true")
    if (backgroundColor) {
      addNavBgColor(backgroundColor)
    }
    bodyEl.classList.add("mobile-menu-open")
    // Adjust the height of the mobile menu on tablet/mobile if the top bar is out of view
    let height: number
    if (window.innerWidth > 767 && window.innerWidth < 1200) {
      if (window.scrollY > topBarHeight) {
        height = window.innerHeight - 59
      } else {
        height = window.innerHeight - 97
      }
      menuEl.style.height = `${height + topBarHeight}px`
    } else if (window.innerWidth <= 767) {
      height = window.innerHeight - 53
      menuEl.style.height = `${height + topBarHeight}px`
    }
    focusLock.on(el)
    navIsActive = true
  }

  function hideMobileMenu() {
    el.classList.remove(mobileActiveClass)
    el.classList.remove(maskActiveClass)
    if (!hamburgerToggleEl || !bodyEl || !menuEl) {
      return
    }
    hamburgerToggleEl.setAttribute("aria-expanded", "false")
    if (backgroundColor && bodyEl.classList.contains("mobile-menu-open")) {
      removeNavBgColor(backgroundColor)
    }
    bodyEl.classList.remove("mobile-menu-open")
    if (window.innerWidth > 767) {
      menuEl.style.height = "auto"
    }
    focusLock.off(menuEl)
    navIsActive = false
  }

  function addNavBgColor(backgroundColor: string) {
    el.classList.add(backgroundColor)
    el.classList.remove(stickyBackgroundColor)
  }

  function removeNavBgColor(backgroundColor: string) {
    el.classList.remove(backgroundColor)
    if (window.scrollY > topBarHeight) {
      el.classList.add(stickyBackgroundColor)
    }
  }

  function loadNavProperties() {
    if (window.scrollY > topBarHeight) {
      removeNavBgColor(backgroundColor)
      el.classList.add(stickyBackgroundColor)
      el.classList.add("sticky-is-stuck")
    } else {
      if (!navIsActive) {
        addNavBgColor(backgroundColor)
        el.classList.remove("sticky-is-stuck")
      }
    }
  }

  function toggleMask() {
    // Close menu on mask click
    const maskEl: HTMLElement | null = document.querySelector(".masthead-mask")
    if (!maskEl) {
      return
    }
  }
}
